import { createSlice } from "@reduxjs/toolkit";
import { fetchRequest } from "../services/api";
import {
  getSendResetLinkRequest,
  getAuthenticateRequest,
  getPasswordResetRequest,
} from "../services/authenticate-requests";

import {
  pushAuthUser,
  clearStoredAuthUser,
  getStoredAuthUser,
} from "../helpers/local-storage-helpers";
import { uiActions } from "./ui-slice";
import history from "../helpers/history-helper";

const ROLE_ADMINISTRATOR = "Administrator";
const ROLE_CUSTOMER = "Customer";

export const ROLES = {
  ROLE_ADMINISTRATOR,
  ROLE_CUSTOMER,
};

const authSlice = createSlice({
  name: "authUser",
  initialState: { authUser: undefined },
  reducers: {
    login(state, action) {
      pushAuthUser(action.payload);
      state.authUser = action.payload;
    },
    logout(state) {
      clearStoredAuthUser();
      state.authUser = undefined;
      history.push("/");
    },
    autologout(state) {
      state.authUser = undefined;
      clearStoredAuthUser();
    },
  },
});

export const authActions = authSlice.actions;

export const autologin = () => {
  return (dispatch) => {
    const authUser = getStoredAuthUser();
    if (authUser) {
      dispatch(loginAndSetAutologout(authUser));
    }
  };
};

const getExpiration = (expiration) => {
  const now = new Date().getTime();
  const expires = new Date(expiration).getTime();
  return expires - now - 60000;
};

const loginAndSetAutologout = (authUser) => {
  return (dispatch) => {
    const expirationInMs = getExpiration(authUser.expiration);
    if (expirationInMs > 0) {
      dispatch(authActions.login(authUser));
      dispatch(autologout(expirationInMs));
    }
  };
};

const autologout = (expirationInMs) => {
  return async (dispatch) => {
    const timeout = async (ms) =>
      new Promise((resolve) => setTimeout(resolve, ms));
    await timeout(expirationInMs);

    dispatch(authActions.autologout());
    dispatch(
      uiActions.enqueNotification({
        message:
          "Authentication token has been invalidated. Please login again!",
        variant: "info",
      })
    );
  };
};

export const authenticateAndGetToken = (login, password) => {
  return async (dispatch) => {
    const authenticateToken = async (login, password) =>
      await fetchRequest(getAuthenticateRequest({ login, password }), false);
    try {      
      const authTokenAndRole = await authenticateToken(login, password);
      const expiryDate = new Date();
      expiryDate.setSeconds(expiryDate.getSeconds() + 3600);
      const expiryDateISO = expiryDate.toISOString();
      dispatch(uiActions.clearNotification());
      dispatch(
        loginAndSetAutologout({
          token: authTokenAndRole.token,
          role: authTokenAndRole.role,
          expiration: expiryDateISO,
        })
      );
    } catch (error) {
      dispatch(
        uiActions.enqueNotification({
          message: "Wrong password or user name",
          variant: "error",
        })
      );
    }
  };
};

export const sendPasswordResetLink = (email) => {
  return async (dispatch) => {
    const sendLink = async (email) => {
      await fetchRequest(getSendResetLinkRequest({ email: email }), false);
    };
    try {
      await sendLink(email);
      dispatch(
        uiActions.enqueNotification({
          message: "Password reset email has been sent",
          variant: "info",
        })
      );
    } catch (error) {
      dispatch(
        uiActions.enqueNotification({
          message: "Could not send password reset link",
          variant: "error",
        })
      );
    }
  };
};

export const resetPassword = (newPassword, token) => {
  return async (dispatch) => {
    const resetPassword = async (newPassword, token) =>
      await fetchRequest(getPasswordResetRequest(newPassword, token), false);
    try {
      await resetPassword(newPassword, token);
      history.push("/");
    } catch (error) {
      dispatch(
        uiActions.enqueNotification({
          message:
            "Sorry! There is something wrong with this link. Please request another reset link.",
          variant: "error",
        })
      );
    }
  };
};

export default authSlice.reducer;
